/**
 * Presentation templates — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_everyday_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const presentationTemplatesPack: ToilePack = {
    slug: 'presentation-templates',
    category: 'office',
    name: 'Presentation templates',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'title', label: 'Title slide', subject: 'a widescreen title slide with the large placeholder "Your Title Here", a smaller subtitle line and a bold colour shape along one edge'},
        {key: 'agenda', label: 'Agenda slide', subject: 'a widescreen slide headed "Agenda" with four numbered placeholder items set in a column and a picture placeholder box beside them'},
        {key: 'chart', label: 'Chart slide', subject: 'a widescreen slide with a short heading line "Chart Title", a simple bar chart of four unlabelled bars and a small key, the rest of the slide clear'},
    ],
    styles: [
        {key: 'corporate', label: 'Corporate blue', style: 'corporate slide design, clean geometric shapes, sans-serif type', palette: 'Deep blue, white and a touch of orange.', framing: 'The slide fills the whole page with a safe margin around the content.'},
        {key: 'playful', label: 'Playful pastel', style: 'playful slide design, rounded shapes and soft shadows', palette: 'Mint, peach and lilac on a cream ground.', framing: 'The slide fills the whole page with a safe margin around the content.'},
        {key: 'dark', label: 'Dark minimal', style: 'dark minimal slide design, thin lines, large type', palette: 'Near-black ground with white type and one neon green accent.', framing: 'The slide fills the whole page with a safe margin around the content.'},
    ],
    directions: ['for kids', 'green theme', 'school project'],
    directionHint: 'try: for kids',
    seo: {
        title: 'Presentation Templates: Title, Agenda and Chart Slides',
        metaDescription: 'Nine slide templates in one design: a title slide, an agenda slide and a chart slide, in corporate blue, playful pastel and dark minimal styles.',
        h1: 'Presentation templates: title, agenda, chart slides',
        tagline: 'Three slides in one design, shown in three styles, ready to start a deck.',
        intro: 'Students and office workers can start a slide deck from a ready design. These nine widescreen slides show one design carried across three layouts: a title slide with a large heading and subtitle, an agenda slide with four numbered items and a picture box, and a chart slide with a simple bar chart and a key. Each layout comes in corporate blue, playful pastel or dark minimal style. Type a request, such as for kids, to shape it.',
        useCases: [
            'a school project presentation',
            'a club or team update',
            'a classroom lesson deck',
            'a first draft for an office talk',
        ],
        faq: [
            {q: 'Are these editable slide files?', a: 'No. They are images of the slides, so you rebuild the design in your own slide software.'},
            {q: 'Do the title, agenda and chart slides match?', a: 'Yes. Within each style they share one design, so a set of them looks like one deck.'},
            {q: 'What are the three styles?', a: 'Corporate blue uses deep blue with an orange touch, playful pastel uses mint, peach and lilac on cream, and dark minimal uses near-black with one neon green accent.'},
        ],
    },
}
